import * as React from 'react';
import { Reply } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getUniversity } from '../../lib/constants';

export type ChatRole = 'researcher' | 'evaluator' | 'reviewer';

interface ChatBubbleProps {
  authorName: string;
  isOwn: boolean;
  timestamp: string;
  parentAuthor?: string;
  parentContent?: string;
  avatarUrl?: string;
  university?: string;
  role?: ChatRole;
  onReply?: () => void;
  children: React.ReactNode;
}

function formatTime(timestamp: string) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffH = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffH < 24) {
    return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function truncate(text: string, max: number) {
  if (text.length <= max) return text;
  return text.slice(0, max) + '...';
}

const roleShapeClass: Record<ChatRole, string> = {
  researcher: 'rounded-full',
  evaluator: 'rounded-lg',
  reviewer: 'rounded-full', // hexagon via clip-path
};

const roleBgFallback: Record<ChatRole, string> = {
  researcher: 'bg-primary/10 text-primary',
  evaluator: 'bg-muted text-muted-foreground',
  reviewer: 'bg-amber-100 text-amber-700',
};

function Avatar({ name, avatarUrl, university, role = 'evaluator' }: { name: string; avatarUrl?: string; university?: string; role?: ChatRole }) {
  const uni = university ? getUniversity(university) : null;
  const bgColor = uni ? `${uni.color} text-white` : roleBgFallback[role];
  const shape = roleShapeClass[role];
  const isHex = role === 'reviewer';

  const baseClass = cn(
    'flex-shrink-0 w-7 h-7 flex items-center justify-center text-[10px] font-bold overflow-hidden',
    !isHex && shape,
    !avatarUrl && bgColor,
    avatarUrl && shape,
  );

  if (avatarUrl) {
    return (
      <div className={baseClass} style={isHex ? { clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' } : undefined}>
        <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div className={baseClass} style={isHex ? { clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' } : undefined}>
      {name.charAt(0)}
    </div>
  );
}

export function ChatBubble({
  authorName,
  isOwn,
  timestamp,
  parentAuthor,
  parentContent,
  avatarUrl,
  university,
  role = 'evaluator',
  onReply,
  children,
}: ChatBubbleProps) {
  const roleLabel = role === 'reviewer' ? '審査員' : role === 'researcher' ? '研究者' : '参加者';
  const uni = university ? getUniversity(university) : null;
  const roleColor = role === 'reviewer' ? 'text-amber-600' : uni ? uni.textColor : 'text-muted-foreground';

  return (
    <div className={cn('flex gap-1.5 mb-1.5', isOwn ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar (other only) */}
      {!isOwn && (
        <div className="mt-0.5">
          <Avatar name={authorName} avatarUrl={avatarUrl} university={university} role={role} />
        </div>
      )}

      <div className={cn('flex flex-col', isOwn ? 'items-end' : 'items-start', 'max-w-[85%] md:max-w-[80%]')}>
        {/* Author name + role + time (other only) */}
        {!isOwn && (
          <div className="flex items-center gap-1.5 mb-0.5 ml-1">
            <span className="text-[11px] font-medium text-muted-foreground">{authorName}</span>
            <span className={cn('text-[9px] font-medium', roleColor)}>{roleLabel}</span>
            <span className="text-[10px] text-muted-foreground/60">{formatTime(timestamp)}</span>
          </div>
        )}

        {/* Bubble */}
        <div
          className={cn(
            'relative px-3 py-1.5 text-sm leading-relaxed whitespace-pre-wrap break-words border',
            isOwn
              ? 'bg-green-50 border-green-200 text-green-900 rounded-tl-xl rounded-tr-sm rounded-bl-xl rounded-br-xl'
              : 'bg-white border-gray-200 text-gray-900 rounded-tl-sm rounded-tr-xl rounded-bl-xl rounded-br-xl'
          )}
        >
          {parentAuthor && (
            <div className="mb-1 p-1 rounded bg-black/5 border-l-2 border-primary/40">
              <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Reply className="h-2.5 w-2.5" />
                {parentAuthor}
              </div>
              {parentContent && (
                <p className="text-[10px] text-muted-foreground line-clamp-1">{truncate(parentContent, 60)}</p>
              )}
            </div>
          )}
          {children}
        </div>

        {/* Time (own) */}
        {isOwn && (
          <div className="flex items-center gap-2 flex-row-reverse">
            <span className="text-[10px] text-muted-foreground/60">{formatTime(timestamp)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
